// frontend/src/components/PipelineMonitor/PipelineSteps.tsx

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import type { PipelineMetric } from '../../interfaces/pipeline.interface';
import './PipelineSteps.css';

interface PipelineStage {
  id: number;
  stage_key: string;
  nome_amigavel: string;
  ordem: number;
  ativo: boolean;
}

interface Props {
  execucao: PipelineMetric[];
}

type StepStatus = 'pending' | 'running' | 'success' | 'failed' | 'skipped';

interface StepState {
  key: string;
  name: string;
  status: StepStatus;
  durationMs: number | null;
  errorMessage: string | null;
}

const getStatusFromEtapa = (etapa: PipelineMetric | undefined): StepStatus => {
  if (!etapa) return 'pending';
  switch (etapa.status) {
    case 'STARTED':
    case 'RUNNING':
      return 'running';
    case 'SUCCESS':
      return 'success';
    case 'FAILED':
      return 'failed';
    case 'SKIPPED':
      return 'skipped';
    default:
      return 'pending';
  }
};

const formatDuration = (ms: number | null): string => {
  if (ms === null) return '';
  if (ms < 1000) return `${ms}ms`;
  const segundos = Math.floor(ms / 1000);
  if (segundos < 60) return `${segundos}s`;
  const minutos = Math.floor(segundos / 60);
  return `${minutos}m ${segundos % 60}s`;
};

const getStatusIcon = (status: StepStatus): string => {
  switch (status) {
    case 'pending': return '⏸️';
    case 'running': return '🔄';
    case 'success': return '✅';
    case 'failed': return '❌';
    case 'skipped': return '⏭️';
  }
};

export const PipelineSteps: React.FC<Props> = ({ execucao }) => {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loadingStages, setLoadingStages] = useState(true);

  useEffect(() => {
    let cancelado = false;
    api.get('/pipeline/stages')
      .then(r => {
        if (cancelado) return;
        const data = r.data?.data;
        if (Array.isArray(data)) {
          setStages(data);
        }
      })
      .catch(() => {
        // sem fallback — se falhar, fica vazio
      })
      .finally(() => {
        if (!cancelado) setLoadingStages(false);
      });
    return () => { cancelado = true; };
  }, []);

  // Agrupa por stage — última linha de cada
  const porStage = new Map<string, PipelineMetric>();
  for (const linha of execucao) {
    const atual = porStage.get(linha.stage);
    if (!atual || new Date(linha.created_at) > new Date(atual.created_at)) {
      porStage.set(linha.stage, linha);
    }
  }

  const steps: StepState[] = stages.map((stage) => {
    const etapa = porStage.get(stage.stage_key);
    return {
      key: stage.stage_key,
      name: stage.nome_amigavel,
      status: getStatusFromEtapa(etapa),
      durationMs: etapa?.duration_ms ?? null,
      errorMessage: etapa?.error_message ?? null,
    };
  });

  return (
    <div className="pipeline-steps-card">
      <div className="pipeline-steps-header">
        <h3>📋 Etapas do Pipeline</h3>
        {loadingStages && <span className="pipeline-steps-progress">Carregando etapas...</span>}
      </div>

      <div className="pipeline-steps-list">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`pipeline-step pipeline-step-${step.status}`}
          >
            <div className="pipeline-step-index">{index + 1}</div>
            <div className="pipeline-step-icon">{getStatusIcon(step.status)}</div>
            <div className="pipeline-step-info">
              <span className="pipeline-step-name">{step.name}</span>
              <span className={`pipeline-step-status pipeline-step-status-${step.status}`}>
                {step.status}
              </span>
              {step.durationMs !== null && (
                <span className="pipeline-step-duration">
                  {formatDuration(step.durationMs)}
                </span>
              )}
            </div>
            {step.errorMessage && (
              <div className="pipeline-step-error">
                ❌ {step.errorMessage}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PipelineSteps;